import { useCallback, useState } from 'react';

import type { ClauseDocxImportOutcome } from '../types';
import { convertDocxToClauseHtml } from '../utils/convert-docx-to-clause-html';
import { isEncryptedDocx, validateDocxFile } from '../utils/validate-file';
import {
  showImportCancelledToast,
  showImportErrorToast,
  showImportSuccessToast,
  showImportWarningToast,
} from '../views/ImportToasts';

export function useClauseDocxImport(outcome: ClauseDocxImportOutcome) {
  const [isConverting, setIsConverting] = useState(false);

  const importDocxFile = useCallback(
    async (file: File): Promise<boolean> => {
      const validationError = validateDocxFile(file);

      if (validationError) {
        showImportErrorToast(validationError);
        return false;
      }

      setIsConverting(true);

      try {
        if (await isEncryptedDocx(file)) {
          showImportErrorToast('encrypted');
          return false;
        }

        const { html, warnings } = await convertDocxToClauseHtml(file);

        if (!html.trim()) {
          showImportErrorToast('corrupt');
          return false;
        }

        const applied = outcome({ html });

        if (applied === 'cancelled') {
          showImportCancelledToast();
          return false;
        }

        showImportSuccessToast();

        if (warnings.length > 0) {
          showImportWarningToast(warnings);
        }

        return true;
      } catch {
        showImportErrorToast('corrupt');
        return false;
      } finally {
        setIsConverting(false);
      }
    },
    [outcome],
  );

  return { importDocxFile, isConverting };
}
